import {
  Box,
  Grid,
  OutlinedInput,
  Typography,
  Autocomplete,
  TextField,
  IconButton,
} from "@mui/material";
import { useEffect, useImperativeHandle, useState, forwardRef } from "react";
import "./EditIdea.scss";
import hospitalsServices from "../../../../services/hospitalsServices";
import usersServices from "../../../../services/usersServices";
import { X } from "lucide-react";
import ideasServices from "../../../../services/ideasServices";

interface EditIdeaProps {
  selectedIdea: any;
  updateContent: (menu: string) => void;
  onSend: (data: any) => void;
}

const EditIdea = forwardRef(
  ({ selectedIdea: idea, updateContent, onSend }: EditIdeaProps, ref) => {
    const [selectedAreas, setSelectedAreas] = useState<any[]>([]);
    const [selectedAuthors, setSelectedAuthors] = useState<any>([]);
    const [areaOptions, setAreaOptions] = useState<any[]>([]);
    const [hospitalOptions, setHospitalOptions] = useState<any[]>([]);
    const [selectedHospitals, setSelectedHospitals] = useState<any[]>([]);
    const [userOptions, setUserOptions] = useState<any[]>([]);
    const [title, setTitle] = useState<string>();
    const [description, setDescription] = useState<string>();
    const [resources, setResources] = useState<string>();
    const [editedIdea, setEditedIdea] = useState<any>(null);

    const body = {
      title,
      description,
      resources,
      areas: selectedAreas.map((a: any) => a.id || a.hospital_area_id),
      authors: selectedAuthors.map((a: any) => a.id),
      hospitals: selectedHospitals.map((h: any) => h.id),
    };

    useImperativeHandle(ref, () => ({
      doSomething() {
        ideasServices.editIdea(idea.idea_id, body).then(() => {
          setEditedIdea({
            ...idea,
            title,
            description,
            resources,
            hospital_units: selectedHospitals,
            hospital_areas: selectedAreas,
            authors: selectedAuthors,
          });
        });
      },
    }));

    useEffect(() => {
      if (editedIdea) {
        onSend(editedIdea);
        updateContent("idea-details");
      }
    }, [editedIdea]);

    useEffect(() => {
      setSelectedAreas(idea.hospital_areas);
      setSelectedHospitals(idea.hospital_units);
      let aux = [];
      for (let author of idea.authors) {
        aux.push({
          name: `${author["first_name"]} ${author["last_name"]}`,
          first_name: author["first_name"],
          last_name: author["last_name"],
          id: author["id"],
        });
      }
      setSelectedAuthors(aux);
      setTitle(idea.title);
      setDescription(idea.description);
      setResources(idea.resources);
    }, []);

    useEffect(() => {
      getAllActiveHospitals();
      getUsersPerHospital();
      getAllActiveAreas();
    }, [selectedHospitals]);

    const getAllActiveHospitals = () => {
      hospitalsServices.getAllActiveHospitals().then((activeHospitals) => {
        let aux = [];
        for (let hospital of activeHospitals) {
          aux.push({
            id: hospital["id"],
            name: hospital["hospital_name"],
          });
        }
        setHospitalOptions(aux);
      });
    };

    const getUsersPerHospital = () => {
      usersServices.getUsersPerHospital(selectedHospitals).then((users) => {
        let aux = [];
        for (let user of users) {
          aux.push({
            name: `${user["first_name"]} ${user["last_name"]}`,
            first_name: user["first_name"],
            last_name: user["last_name"],
            id: user["id"],
            hospital: user["hospital"],
          });
        }
        setUserOptions(aux);
      });
    };

    const getAllActiveAreas = () => {
      hospitalsServices.getAllActiveAreas().then((areas) => {
        let aux = [];
        for (let area of areas) {
          aux.push({
            id: area["id"],
            area_name: area["area_name"],
          });
        }
        setAreaOptions(aux);
      });
    };

    const removeAuthor = (author: any) =>
      setSelectedAuthors(
        selectedAuthors.filter((a: any) => a.id !== author.id)
      );

    return (
      <Box>
        <Box className="menu-content">
          <Box className="menu-content__idea">
            <Grid container spacing={2} sx={{ width: "100%" }}>
              <Grid size={1} className="grid-container">
                <Typography>Nombre de idea</Typography>
              </Grid>
              <Grid size={10}>
                <OutlinedInput
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid size={1} className="grid-container">
                <Typography>Unidad</Typography>
              </Grid>
              <Grid size={4}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={hospitalOptions}
                  value={selectedHospitals}
                  onChange={(_, newValue) => setSelectedHospitals(newValue)}
                  filterSelectedOptions
                  size="small"
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  getOptionLabel={(o) => o.name}
                  renderTags={(selected) =>
                    selected.map((h) => h.name).join(", ")
                  }
                  renderInput={(params) => <TextField {...params} />}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.name}
                    </li>
                  )}
                  disablePortal
                  slotProps={{
                    popper: {
                      modifiers: [
                        { name: "offset", options: { offset: [0, 55] } },
                      ],
                    },
                  }}
                />
              </Grid>
              <Grid size={1} sx={{ textAlign: "end" }}>
                <Typography>Área</Typography>
              </Grid>
              <Grid size={5}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={areaOptions}
                  value={selectedAreas}
                  onChange={(_, newValue) => setSelectedAreas(newValue)}
                  filterSelectedOptions
                  size="small"
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  getOptionLabel={(o) => o.area_name}
                  renderTags={(selected) =>
                    selected.map((a) => a.area_name).join(", ")
                  }
                  renderInput={(params) => <TextField {...params} />}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.area_name}
                    </li>
                  )}
                  disablePortal
                  slotProps={{
                    popper: {
                      modifiers: [
                        { name: "offset", options: { offset: [0, 55] } },
                      ],
                    },
                  }}
                />
              </Grid>
              <Grid size={1} className="grid-container">
                <Typography>Autor(es)</Typography>
              </Grid>
              <Grid size={10}>
                <Autocomplete
                  multiple
                  size="small"
                  options={userOptions}
                  value={selectedAuthors}
                  onChange={(_, newValue) => setSelectedAuthors(newValue)}
                  filterSelectedOptions
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  getOptionLabel={(o) => o.name}
                  renderTags={(selected) =>
                    selected.map((author) => (
                      <Box
                        key={author.id}
                        className="form__container__info__select__labels"
                      >
                        <Typography
                          sx={{ fontSize: "18px !important" }}
                          className="form__container__info__select__labels-text"
                        >
                          {author.name}
                        </Typography>

                        <IconButton
                          size="small"
                          className="form__container__info__select__labels-button"
                          onClick={() => removeAuthor(author)}
                        >
                          <X
                            size={22}
                            style={{ color: "#41074b", marginTop: "1px" }}
                          />
                        </IconButton>
                      </Box>
                    ))
                  }
                  renderInput={(params) => <TextField {...params} />}
                  disablePortal
                  slotProps={{
                    popper: {
                      modifiers: [
                        { name: "offset", options: { offset: [0, 55] } },
                      ],
                    },
                  }}
                />
              </Grid>
              <Grid size={1} className="grid-container">
                <Typography>Descripción</Typography>
              </Grid>
              <Grid size={10}>
                <OutlinedInput
                  multiline
                  fullWidth
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  sx={{
                    alignItems: "flex-start",
                    overflow: "auto",
                  }}
                ></OutlinedInput>
              </Grid>
              <Grid size={1} className="grid-container">
                <Typography>Recursos necesarios</Typography>
              </Grid>
              <Grid size={10}>
                <OutlinedInput
                  multiline
                  fullWidth
                  value={resources}
                  onChange={(e) => setResources(e.target.value)}
                  sx={{
                    alignItems: "flex-start",
                    overflow: "auto",
                  }}
                ></OutlinedInput>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    );
  }
);
export default EditIdea;
